import { AfterContentChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Select, Store } from '@ngxs/store';
import { Compass, CompassAnswerable, CompassAnswerableType, CompassScoreGroup, CompassTrackable, CompassTrackableIntegrationType, CompassTrackableIntegrationTypeUtil, CompassTrackableScaleType, GetCompassTags, GetReviewCompasses, Pair, LoadingList, TrackableScaleRange, TrackableValuation, CompassProduct, GetCompassProducts, GetProductCompass } from 'huddyapi';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { CompassReviewDialog, CompassReviewDialogData } from '../../components/compass-review-dialog/compass-review-dialog.component';
import { DesktopErrorHandler } from '../../services/errorHandler.service';

interface ListedItemInfo {
  index: number;
  pageState: CompassPageState;
}

@Component({
  selector: 'app-review',
  templateUrl: './review.component.html',
  styleUrls: ['../builder.component.scss']
})
export class ReviewComponent implements OnInit, OnDestroy, AfterContentChecked {
  @Select(state => state.compassState.productCompasses) productCompasses$: Observable<{ [productId: number]: Compass }>;
  @Select(state => state.compassState.reviewCompasses) reviewCompasses$: Observable<LoadingList<Compass>>;
  @Select(state => state.compassState.compassProducts) compassProducts$: Observable<LoadingList<CompassProduct>>;

  @ViewChild('scrollableContent', { static: false }) scrollableContent: ElementRef;
  @ViewChild('sectionId', { static: false }) sectionId: ElementRef;

  productCompassSubscription: Subscription;
  reviewCompassSubscription: Subscription;
  compassProductsSubscription: Subscription;
  selectedId: number;
  listingInfo: { [sidebarTab: string]: Map<number, ListedItemInfo> };
  productCompasses: { [productId: number]: Compass };
  reviews: LoadingList<Compass>;
  products: LoadingList<CompassProduct>;
  loadingCategories: BehaviorSubject<boolean>;
  submitting: boolean;
  lastSectionId: Pair<number, number>;
  sidebarTab: string;
  _filterText: string;
  normalizedFilterText: string;
  filteredReviews: Compass[];
  filteredProducts: CompassProduct[];

  set filterText(filterText: string) {
    if(filterText == null)
      filterText = '';
    this._filterText = filterText;
    this.normalizedFilterText = this.normalizeText(filterText);
    this.applyFilter();
  }

  get filterText(): string {
    return this._filterText;
  }

  constructor(private store: Store, private errorHandler: DesktopErrorHandler, private dialog: MatDialog) {
    this.lastSectionId = new Pair<number, number>(null, null);
    this.listingInfo = {
      review: new Map<number, ListedItemInfo>(),
      modify: new Map<number, ListedItemInfo>()
    };
    this.selectedId = null;
    this.productCompasses = {};
    this.reviews = {
      list: [],
      loaded: false,
      loading: true
    };
    this.products = {
      list: [],
      loaded: false,
      loading: true
    }
    this.loadingCategories = new BehaviorSubject<boolean>(false);
    this.submitting = false;
    this.sidebarTab = 'review';
    this.filterText = '';
    this.filteredReviews = [];
    this.filteredProducts = [];
  }

  ngOnInit(): void {
    this.productCompassSubscription = this.productCompasses$.subscribe((productCompasses: { [productId: number]: Compass }) => {
      this.productCompasses = productCompasses;
    });
    this.reviewCompassSubscription = this.reviewCompasses$.subscribe((state: LoadingList<Compass>) => {
      this.reviews = state;
      if(!this.reviews.loading) {
        this.listingInfo.review = new Map<number, ListedItemInfo>(this.reviews.list.map((compass: Compass, index: number) => [compass.id, { index: index, pageState: new CompassPageState(this.listingInfo.review.get(compass.id)?.pageState) }]));
        this.applyReviewFilter();
        if(this.sidebarTab == 'review')
          this.autoSelect();
      }
    });
    this.compassProductsSubscription = this.compassProducts$.subscribe((state: LoadingList<CompassProduct>) => {
      this.products = state;
      if(!this.products.loading) {
        this.listingInfo.modify = new Map<number, ListedItemInfo>(this.products.list.map((product: CompassProduct, index: number) => [product.id, { index: index, pageState: new CompassPageState(this.listingInfo.modify.get(product.id)?.pageState) }]));
        this.applyProductFilter();
        if(this.sidebarTab == 'modify')
          this.autoSelect();
      }
    });
    this.update(false);
  }

  ngOnDestroy(): void {
    this.productCompassSubscription?.unsubscribe();
    this.reviewCompassSubscription?.unsubscribe();  
    this.compassProductsSubscription?.unsubscribe();
  }

  ngAfterContentChecked(): void {
    if(this.sectionId?.nativeElement) {
      const sectionIdArr: number[] = this.sectionId.nativeElement.innerText.split(',').map((str: string): number => parseInt(str));
      if(sectionIdArr[0] != this.lastSectionId.a || sectionIdArr[1] != this.lastSectionId.b) {
        this.lastSectionId.a = sectionIdArr[0];
        this.lastSectionId.b = sectionIdArr[1];
        if(sectionIdArr[0] == this.selectedId) {
          const selected: Compass | CompassProduct = this.getSelected();
          if(!selected)
            return;
          const tab: number = this.getTab(selected.id);
          if(selected && tab == sectionIdArr[1])
            this.scrollableContent?.nativeElement?.scrollTo(0, this.getScrollTop(tab, selected.id));
        }
      }
    }
  }

  setSidebarTab(tab: string, autoSelect: boolean = true): void {
    this.select(null);
    this.sidebarTab = tab;
    if(autoSelect)
      this.autoSelect();
  }

  getLoadingList(sidebarTab: string = this.sidebarTab): LoadingList<Compass> | LoadingList<CompassProduct> {
    return sidebarTab == 'review' ? this.reviews : this.products;
  }

  getFilteredItems() {
    return this.sidebarTab == 'review' ? this.filteredReviews : this.filteredProducts;
  }

  autoSelect(): void {
    const filteredItems: Compass[] | CompassProduct[] = this.getFilteredItems();
    if(this.selectedId != null) {
      if(filteredItems.length == 0) {
        this.select(null);
        return;
      }
      const selected: Compass | CompassProduct = this.getSelected();
      if(selected && this.itemMatchesNormalizedText(selected, this.normalizedFilterText) && filteredItems.findIndex((item: Compass | CompassProduct): boolean => item.id == selected.id) >= 0)
        return;
    }
    if(filteredItems.length > 0)
      this.select(filteredItems[0].id);
    else
      this.select(null);
  }

  normalizeText(text: string): string {
    return (text == null ? '' : text).toLowerCase().replace(/\s/g, '');
  }

  stringMatchesNormalizedText(str: string, normalizedText: string): boolean {
    return this.normalizeText(str || '').includes(normalizedText || '');
  }

  itemMatchesNormalizedText(item: Compass | CompassProduct, normalizedText: string): boolean {
    return this.stringMatchesNormalizedText(item.name, this.normalizedFilterText)
      || this.stringMatchesNormalizedText(item.providerName, this.normalizedFilterText);
  }

  applyReviewFilter(): void {
    this.filteredReviews = this.normalizedFilterText.length == 0 ? this.reviews.list : this.reviews.list.filter((item: Compass): boolean => this.itemMatchesNormalizedText(item, this.normalizedFilterText));
  }

  applyProductFilter(): void {
    this.filteredProducts = this.normalizedFilterText.length == 0 ? this.products.list : this.products.list.filter((item: CompassProduct): boolean => this.itemMatchesNormalizedText(item, this.normalizedFilterText));
  }

  applyFilter(): void {
    this.applyReviewFilter();
    this.applyProductFilter();
    this.autoSelect();
  }

  updateScrollTop() {
    if(this.sectionId?.nativeElement) {
      const sectionIdArr: number[] = this.sectionId.nativeElement.innerText.split(',').map((str: string): number => parseInt(str));
      if(sectionIdArr[0] == this.selectedId) {
        const selected: Compass | CompassProduct = this.getSelected();
        if(!selected)
          return;
        const tab: number = this.getTab(selected.id);
        if(selected && tab == sectionIdArr[1])
          this.setScrollTop(tab, this.scrollableContent?.nativeElement?.scrollTop || 0, selected.id);
      }
    }
  }

  getItemInfo(id: number, sidebarTab: string): ListedItemInfo {
    if(id == null)
      return;
    const itemInfoMap: Map<number, ListedItemInfo> = this.listingInfo[sidebarTab];
    return itemInfoMap?.get(id);
  }

  getTab(id: number = this.getSelected()?.id, sidebarTab: string = this.sidebarTab): number {
    return this.getItemInfo(id, sidebarTab)?.pageState?.tab;
  }

  setTab(tab: number, id: number = this.getSelected()?.id, sidebarTab: string = this.sidebarTab): void {
    let itemInfo: ListedItemInfo = this.getItemInfo(id, sidebarTab);
    if(itemInfo?.pageState) {
      this.updateScrollTop();
      itemInfo.pageState.tab = tab;
    }
  }

  getScrollTop(tab: number, id: number = this.getSelected()?.id, sidebarTab: string = this.sidebarTab): number {
    const pageState: CompassPageState = this.getItemInfo(id, sidebarTab)?.pageState;
    return pageState ? pageState.scrollTops[tab] : 0;
  }

  setScrollTop(tab: number, value: number, id: number = this.getSelected()?.id, sidebarTab: string = this.sidebarTab): void {
    let pageState: CompassPageState = this.getItemInfo(id, sidebarTab)?.pageState;
    if(pageState)
      pageState.scrollTops[tab] = value;
  }

  update(force: boolean = true): void {
    if(!this.reviews.loading && (force || !this.reviews.loaded))
      this.store.dispatch(new GetReviewCompasses()).toPromise().catch((error: any) => this.errorHandler.handle(error, 'compasses'));
    if(!this.products.loading && (force || !this.products.loaded))
      this.store.dispatch(new GetCompassProducts()).toPromise().catch((error: any) => this.errorHandler.handle(error, 'products'));
    this.updateCategories();
  }

  updateCategories(): void {
    if(this.loadingCategories.getValue())
      return;
    this.loadingCategories.next(true);
    this.store.dispatch(new GetCompassTags()).subscribe(() => {
      this.loadingCategories.next(false);
    }, (error: any) => {
      this.loadingCategories.next(false);
      this.errorHandler.handle(error, 'compass categories');
    });
  }

  isRefreshing(): boolean {
    return (this.reviews.loading && this.reviews.loaded) || (this.products.loading && this.products.loaded);
  }

  loadCompass(product: CompassProduct): void {
    if(!product)
      return;
    this.store.dispatch(new GetProductCompass(product)).toPromise().catch((error: any) => this.errorHandler.handle(error, 'compass'));
  }

  select(id: number, sidebarTab: string = this.sidebarTab): void {
    this.updateScrollTop();
    if(sidebarTab != this.sidebarTab)
      this.setSidebarTab(sidebarTab, false);
    this.selectedId = id;
    if(sidebarTab == 'modify' && this.productCompasses[id] === undefined) {
      this.productCompasses[id] = null;
      this.loadCompass(this.getSelected() as CompassProduct);
    }
  }

  isSelected(id: number, sidebarTab: string = this.sidebarTab): boolean {
    return id != null && this.selectedId == id && this.sidebarTab == sidebarTab;
  }

  getSelected(): Compass | CompassProduct {
    if(this.selectedId == null)
      return;
    const index: number = this.listingInfo[this.sidebarTab].get(this.selectedId)?.index;
    return index == null ? null : this.getLoadingList().list[index];
  }

  getSelectedCompass(): Compass {
    const selected: Compass | CompassProduct = this.getSelected();
    if(!selected)
      return null;
    if(this.sidebarTab == 'review')
      return selected as Compass;
    else
      return this.productCompasses[(selected as CompassProduct).id];
  }

  getEmptyStateTitle(sidebarTab: string = this.sidebarTab): string {
    return sidebarTab == 'review' ? 'No review compasses' : 'No compass products';
  }

  getEmptyStateDesc(sidebarTab: string = this.sidebarTab): string {
    return sidebarTab == 'review' ? 'There are no compasses for you to review' : 'There are no compass products for you to modify'; 
  }

  getVersion(item: Compass | CompassProduct): string {
    return 'Version ' + (item instanceof Compass ? item.version : item.compassVersion);
  }

  makeReadableText(text: string): string {
    if(!(text?.length > 0))
      return '';
    text = text.replace(/_/g, ' ').trim();
    return (text.charAt(0).toLocaleUpperCase() + text.substring(1).toLocaleLowerCase());
  }

  trackableIntegrationsToString(integrations: CompassTrackableIntegrationType[]): string {
    return this.stringsToString((integrations || []).map((integration: CompassTrackableIntegrationType): string => CompassTrackableIntegrationTypeUtil.getIntegrationDisplayValue(integration)));
  }

  stringsToString(strings: string[]): string {
    return (strings || []).join(', ');
  }

  makeReadableBoolean(bool: boolean): string {
    return bool ? 'Yes' : 'No';
  }

  isEvidence(trackable: CompassTrackable): boolean {
    return trackable.trackableType == 'EVIDENCE';
  }

  hasMultipleGroups(compass: Compass): boolean {
    return compass.scoreSummaryGroups?.length > 1;
  }

  getGroupNames(compass: Compass, groupGuids: string[]): string {
    const guidMap: Map<string, void> = new Map<string, void>((groupGuids || []).map((guid: string) => [guid, null]));
    return this.stringsToString((compass?.scoreSummaryGroups || []).filter((group: CompassScoreGroup): boolean => guidMap.has(group.guid)).map((group: CompassScoreGroup): string => group.name));
  }

  isCount(range: TrackableScaleRange): boolean {
    return range.type == CompassTrackableScaleType.COUNT;
  }

  answerableIsButton(answerable: CompassAnswerable): boolean {
    return answerable.answerType == CompassAnswerableType.BUTTON;
  }

  usesCustomScoreClass(compass: Compass): boolean {
    return compass.scoreClass != Compass.STANDARD_SCORE_CLASS;
  }

  getValuationScaleType(valuation: TrackableValuation): CompassTrackableScaleType {
    if(valuation.scaleType != null)
      return valuation.scaleType;
    for(let scale of (valuation.scale || [])) {
      if(scale.type != null)
        return scale.type;
    }
    return null;
  }

  submit(approve: boolean = true): void {
    if(this.submitting || !this.getSelectedCompass())
      return;
    this.submitting = true;
    let dialogData: CompassReviewDialogData;
    switch(this.sidebarTab) {
      case 'review':
        dialogData = {
          approve: approve,
          compass: this.getSelected() as Compass,
          loadingSubject: approve ? this.loadingCategories : null
        };
        break;
      case 'modify':
        dialogData = {
          approve: true,
          compass: this.getSelectedCompass(),
          loadingSubject: this.loadingCategories,
          product: this.getSelected() as CompassProduct
        };
        break;
      default:
    }
    this.dialog.open(CompassReviewDialog, {
      width: '636px',
      data: dialogData
    }).afterClosed().subscribe(() => this.submitting = false);
  }
}

class CompassPageState {
  tab: number;
  scrollTops: number[];

  constructor(pageState: CompassPageState) {
    this.tab = pageState?.tab || 0;
    this.scrollTops = pageState?.scrollTops || [0, 0, 0, 0];
  }
}